import { useMemo } from "react";
import { KanbanDefaultStates } from "@/components/ProjectList/CanvasKanban";
import { Issue } from "@/types/issues";
import { STATUS_CONSTANTS } from "../constants";
import { toast } from "sonner";
import { StatusGroupType, StatusInterface } from "../StatusesContent";
import { ProjectStatus } from "@/components/ProjectConfiguration";
import { getColumnConfig } from "../utils/columnConfigUtils";
interface StatusGroupsHook {
  projectStatusGroups: StatusGroupType[];
}
interface UseStatusGroupsProps {
  currentProjectIssues: Record<string, Issue>;
  customProjectStatuses: ProjectStatus[];
}

export const useStatusGroups = ({
  currentProjectIssues,
  customProjectStatuses,
}: UseStatusGroupsProps): StatusGroupsHook => {
  const processedIssues = useMemo(() => {
    if (!currentProjectIssues) return null;

    const currentProjectIssuesArray = Object.values(currentProjectIssues);
    const statusCounts = new Map<string, number>();

    currentProjectIssuesArray.forEach((issue: Issue) => {
      const prd = issue.featurePrd;
      let status = prd?.status || KanbanDefaultStates.BACKLOG;

      const customStatus = customProjectStatuses.find((s) => s.name === status);

      if (
        !Object.values(KanbanDefaultStates).includes(
          status as KanbanDefaultStates,
        ) &&
        !customStatus
      ) {
        status = KanbanDefaultStates.BACKLOG;
      }

      statusCounts.set(status, (statusCounts.get(status) || 0) + 1);
    });

    return { statusCounts };
  }, [currentProjectIssues, customProjectStatuses]);

  const projectStatusGroups = useMemo((): StatusGroupType[] => {
    const columnConfig = getColumnConfig(customProjectStatuses);
    const { statusCounts } = processedIssues || {
      statusCounts: new Map<string, number>(),
    };

    const defaultStatusNameMap = new Map<string, string>();
    columnConfig.forEach((config) => {
      if (config.isDefault) {
        const defaultStatus = customProjectStatuses.find(
          (s) =>
            s.isDefault &&
            (s.id === config.key || s.defaultStatusKey === config.key),
        );

        let originalDefaultName: string | undefined;
        if (defaultStatus?.defaultStatusKey) {
          originalDefaultName =
            KanbanDefaultStates[
              defaultStatus.defaultStatusKey as keyof typeof KanbanDefaultStates
            ];
        } else {
          originalDefaultName =
            KanbanDefaultStates[config.key as keyof typeof KanbanDefaultStates];
        }
        if (originalDefaultName && originalDefaultName !== config.name) {
          defaultStatusNameMap.set(originalDefaultName, config.name);
        }
        defaultStatusNameMap.set(config.name, config.name);
      }
    });

    const aggregatedCounts = new Map<string, number>();
    statusCounts.forEach((count, statusName) => {
      const currentName = defaultStatusNameMap.get(statusName) || statusName;
      aggregatedCounts.set(
        currentName,
        (aggregatedCounts.get(currentName) || 0) + count,
      );
    });

    const typeGroups = new Map<
      string,
      Array<{ config: (typeof columnConfig)[0]; count: number }>
    >();

    columnConfig.forEach((config) => {
      const statusName = config.name;
      const count = aggregatedCounts.get(statusName) || 0;

      if (!typeGroups.has(config.type)) {
        typeGroups.set(config.type, []);
      }
      typeGroups.get(config.type)!.push({ config, count });
    });

    return Array.from(typeGroups.entries()).map(([type, items]) => {
      const statusItems: StatusInterface[] = items.map(({ config, count }) => ({
        id: config.key,
        name: config.name,
        count,
        color: { value: STATUS_CONSTANTS.DEFAULT_COLOR },
        isDefault: config.isDefault,
      }));

      const totalCount = statusItems.reduce((sum, item) => sum + item.count, 0);
      const groupName =
        items.find(({ config }) => config.isDefault)?.config.name || type;

      return {
        type,
        name: groupName,
        count: totalCount,
        statuses: statusItems,
      };
    });
  }, [processedIssues, customProjectStatuses]);

  return { projectStatusGroups };
};
